import { useQuery } from '@tanstack/react-query';
import { REVIEW_RESULT_VIEW, useSelectedReviewResultView } from 'reducers/reviewResultSlice';
import {
  flattenGoalQuestions,
  mergeGoalQuestionsByGoalId,
} from 'screens/performanceReview/components/GoalForm/hooks/dataHooks';
import { useReviewPreviewPageStatus, useReviewPreviewParams } from 'screens/performanceReview/hooks/reviewPreviewHooks';
import useReviewFormParams from 'screens/performanceReview/hooks/useReviewFormParams';

import { REACT_QUERY_KEYS } from 'constants/reactQueryKeys';

import { getGoalForm, getGoalFormV1, getReviewResponseResultV1 } from 'services/performanceReview';

const getData = async ({
  reviewId,
  employeeId,
  providerId,
  responseId,
  providerType,
  isPreview,
  previewSubmissionStatus,
}) => {
  try {
    const res = await getGoalForm({
      reviewId,
      employeeId,
      providerId,
      responseId,
      providerType,
      isPreview,
      previewSubmissionStatus,
    });
    const goalQuestions = res?.goalQuestions ?? [];
    const flatGoalQuestion = flattenGoalQuestions(goalQuestions);
    // merge goal questions by goal id if applicable
    const mergedGoalQuestions = mergeGoalQuestionsByGoalId(flatGoalQuestion);

    return { ...res, mergedGoalQuestions };
  } catch (err) {
    console.error('Error fetching goal form data : ', err);
  }
  return null;
};

const getDataV1 = async ({ reviewId, employeeId, providerType, isPreview, previewSubmissionStatus }) => {
  try {
    const res = await getGoalFormV1({
      reviewId,
      employeeId,
      providerType,
      isPreview,
      previewSubmissionStatus,
    });

    const goalQuestions = res?.goalQuestions ?? [];
    const flatGoalQuestion = flattenGoalQuestions(goalQuestions);
    // merge goal questions by goal id if applicable
    const mergedGoalQuestions = mergeGoalQuestionsByGoalId(flatGoalQuestion);

    return { ...res, mergedGoalQuestions };
  } catch (err) {
    console.error('Error fetching goal form data : ', err);
  }
  return null;
};

export const useGoalResultData = ({ reviewId, employeeId, providerId, responseId, providerType }) => {
  const selectedView = useSelectedReviewResultView();
  const { isPreview, providerType: previewResponseProviderType } = useReviewPreviewParams();
  const previewSubmissionStatus = useReviewPreviewPageStatus();
  // for anonymous we use reviewId instead of providerId

  return useQuery(
    [REACT_QUERY_KEYS.ReviewGoalResultData, reviewId, employeeId, providerId, responseId],
    () => {
      if (selectedView === REVIEW_RESULT_VIEW.PEOPLE) {
        return getData({
          reviewId,
          employeeId,
          providerId: responseId ? null : providerId,
          responseId,
          // in case of view by people, we need to use the provider type for specific reviewer
          providerType,
          isPreview,
          previewSubmissionStatus,
        });
      }
      return getDataV1({
        reviewId,
        employeeId,
        // but in case of preview, we need to use the provider type from preview query params
        providerType: previewResponseProviderType,
        isPreview,
        previewSubmissionStatus,
      });
    },
    { enabled: !!employeeId }
  );
};

const getResponseDataV1 = async ({
  employeeId,
  reviewId,
  goalId,
  competencyId,
  questionId,
  excludeEmployeeId,
  providerType,
  isPreview,
  previewSubmissionStatus,
}) => {
  try {
    const res = await getReviewResponseResultV1({
      employeeId,
      reviewId,
      goalId,
      competencyId,
      questionId,
      excludeEmployeeId,
      providerType,
      isPreview,
      previewSubmissionStatus,
    });
    return res;
  } catch (err) {
    console.error('Error fetching goal form response data : ', err);
  }
  return null;
};

export const useReviewResponseGoalData = ({ goalId, questionId, excludeEmployeeId }) => {
  const { isPreview, providerType } = useReviewPreviewParams();
  const previewSubmissionStatus = useReviewPreviewPageStatus();

  const { employeeId, reviewId } = useReviewFormParams();
  return useQuery(
    [REACT_QUERY_KEYS.ReviewResponseGoalResultData, employeeId, reviewId, goalId, questionId, excludeEmployeeId],
    () =>
      getResponseDataV1({
        goalId,
        reviewId,
        employeeId,
        questionId,
        excludeEmployeeId,
        providerType,
        isPreview,
        previewSubmissionStatus,
      })
  );
};
